import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  class=\'color-picker {{if this.isOpen \"is-open\"}}\'\n  {{did-insert this.registerOutside}}\n  {{will-destroy this.unregisterOutside}}\n  ...attributes\n>\n  <button\n    type=\'button\'\n    class=\'color-picker__trigger {{unless this.hasColor \"is-empty\"}}\'\n    style={{this.triggerStyle}}\n    title={{t \'color\'}}\n    data-test-bb-color-picker-toggle\n    {{on \'click\' this.toggle}}\n  ></button>\n\n  {{#if this.isOpen}}\n    <div class=\'color-picker__panel\'>\n      <div class=\'color-picker__grid\'>\n        {{#each this.swatches as |swatch|}}\n          <button\n            type=\'button\'\n            class=\'color-picker__swatch {{if swatch.selected \"is-selected\"}}\'\n            style={{swatch.style}}\n            title={{swatch.hex}}\n            data-test-select-color\n            {{on \'click\' (fn this.selectColor swatch.hex)}}\n          ></button>\n        {{/each}}\n      </div>\n\n      <div class=\'color-picker__footer\'>\n        <label class=\'color-picker__custom\' title={{t \'color\'}}>\n          <input\n            type=\'color\'\n            value={{this.customValue}}\n            data-test-on-custom-input\n            {{on \'input\' this.onCustomInput}}\n          />\n        </label>\n\n        <button\n          type=\'button\'\n          class=\'color-picker__remove\'\n          title={{t \'reset default color\'}}\n          data-test-remove\n          {{on \'click\' this.remove}}\n        >\n          <FaIcon @icon=\'trash-alt\' />\n        </button>\n      </div>\n    </div>\n  {{/if}}\n</div>");

var _class, _descriptor;
const DEFAULT_COLORS = [
// greys
'#000000', '#444444', '#666666', '#999999', '#cccccc', '#eeeeee', '#f3f3f3', '#ffffff',
// primaries
'#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#9900ff', '#ff00ff',
// tint/shade ramps (lightest -> darkest)
'#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#cfe2f3', '#d9d2e9', '#ead1dc', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#9fc5e8', '#b4a7d6', '#d5a6bd', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6fa8dc', '#8e7cc3', '#c27ba0', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3d85c6', '#674ea7', '#a64d79', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#0b5394', '#351c75', '#741b47', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#073763', '#20124d', '#4c1130'];

/**
 * Native, dependency-free color picker.
 *
 * Collapsed by default: shows a single trigger with the currently selected
 * color, or a black/white square when nothing is selected (signalling the user
 * must click to choose). Clicking opens the preset swatches plus a native
 * `<input type="color">` for the full palette and a remove (trash) action.
 * Picking a swatch or removing collapses the panel.
 *
 * Args:
 *  - `@color`     current value (hex string)
 *  - `@onChange`  called with the new hex (or null when removed)
 *  - `@palette`   optional array of hex strings to replace the defaults
 */
let BbColorPickerComponent = (_class = class BbColorPickerComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "isOpen", _descriptor, this);
    this._onOutside = null;
  }
  get colors() {
    return this.args.palette || DEFAULT_COLORS;
  }
  get hasColor() {
    return !!this.args.color;
  }

  // Solid background for the trigger when a color is selected; empty otherwise
  // (CSS paints the black/white "no color" affordance).
  get triggerStyle() {
    return this.hasColor ? htmlSafe(`background-color: ${this.args.color}`) : undefined;
  }

  // Native <input type="color"> requires a valid #rrggbb value.
  get customValue() {
    return this.args.color || '#000000';
  }
  get swatches() {
    const current = (this.args.color || '').toLowerCase();
    return this.colors.map(hex => ({
      hex,
      selected: hex.toLowerCase() === current,
      style: htmlSafe(`background-color: ${hex}`)
    }));
  }
  update(color) {
    this.args.onChange?.(color);
  }
  registerOutside(element) {
    this._onOutside = event => {
      if (this.isOpen && !element.contains(event.target)) {
        this.isOpen = false;
      }
    };
    // Capture phase so this runs before the trigger's own click handler.
    document.addEventListener('click', this._onOutside, true);
  }
  unregisterOutside() {
    if (this._onOutside) {
      document.removeEventListener('click', this._onOutside, true);
    }
  }
  toggle() {
    this.isOpen = !this.isOpen;
  }
  selectColor(hex) {
    this.update(hex);
    this.isOpen = false;
  }
  onCustomInput(event) {
    this.update(event.target.value);
  }
  remove() {
    this.update(null);
    this.isOpen = false;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "isOpen", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "registerOutside", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerOutside"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unregisterOutside", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unregisterOutside"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectColor", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectColor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onCustomInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onCustomInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "remove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "remove"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbColorPickerComponent);

export { BbColorPickerComponent as default };
//# sourceMappingURL=bb-color-picker.js.map
